/* @vendored-from tactic:src/modules/greenhouse/lib/geometry/polygon.ts kernel-v1
 * NO EDITAR EN PEST: este archivo se regenera con scripts/sync-greenhouse-kernel.mjs
 * desde el repo de Tactic (fuente de verdad del kernel geométrico). */
import type { Point2D, GreenhouseShapeType, GreenhouseContourGeometry } from "../../types/editor";
import { closeRing, normalizeRing, calculateSegmentLength, calculateBounds } from "./core";

/** Calculate polygon area using Shoelace formula (returns absolute value in m²) */
export function calculatePolygonArea(points: Point2D[]): number {
  const ring = closeRing(normalizeRing(points));
  if (ring.length < 4) return 0; // need at least 3 distinct + closing
  let area = 0;
  for (let i = 0; i < ring.length - 1; i++) {
    area += ring[i].x * ring[i + 1].y;
    area -= ring[i + 1].x * ring[i].y;
  }
  return Math.abs(area) / 2;
}

/** Calculate polygon perimeter in meters */
export function calculatePolygonPerimeter(points: Point2D[]): number {
  const ring = closeRing(normalizeRing(points));
  let perimeter = 0;
  for (let i = 0; i < ring.length - 1; i++) {
    perimeter += calculateSegmentLength(ring[i], ring[i + 1]);
  }
  return perimeter;
}

/** Infer shape type from geometry */
export function inferShapeType(geometry: GreenhouseContourGeometry | null): GreenhouseShapeType {
  if (!geometry) return "rectangular";
  if (geometry.innerHoles.length > 0) return "irregular_with_holes";
  
  const pts = normalizeRing(geometry.outerRing.points);
  if (pts.length < 3) return "rectangular";
  
  // Check if it's a rectangle/square (4 points, right angles)
  if (pts.length === 4) {
    const sides = [];
    const closed = closeRing(pts);
    for (let i = 0; i < 4; i++) {
      sides.push(calculateSegmentLength(closed[i], closed[i + 1]));
    }
    
    // Check right angles via dot product
    let allRight = true;
    for (let i = 0; i < 4; i++) {
      const a = closed[i];
      const b = closed[(i + 1) % 4];
      const c = closed[(i + 2) % 4];
      const dot = (b.x - a.x) * (c.x - b.x) + (b.y - a.y) * (c.y - b.y);
      if (Math.abs(dot) > 0.5) { allRight = false; break; }
    }
    
    if (allRight) {
      const ratio = Math.min(sides[0], sides[1]) / Math.max(sides[0], sides[1]);
      return ratio > 0.95 ? "square" : "rectangular";
    }
  }
  
  return "irregular";
}

/** Check if a point is inside a polygon (ray casting algorithm) */
export function isPointInPolygon(point: Point2D, polygon: Point2D[]): boolean {
  const ring = closeRing(normalizeRing(polygon));
  let inside = false;
  for (let i = 0, j = ring.length - 2; i < ring.length - 1; j = i++) {
    const xi = ring[i].x, yi = ring[i].y;
    const xj = ring[j].x, yj = ring[j].y;
    const intersect =
      yi > point.y !== yj > point.y &&
      point.x < ((xj - xi) * (point.y - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

/** Calculate the net area (outer minus holes) */
export function calculateNetArea(geometry: GreenhouseContourGeometry): number {
  let area = calculatePolygonArea(geometry.outerRing.points);
  for (const hole of geometry.innerHoles) {
    area -= calculatePolygonArea(hole.points);
  }
  return Math.max(0, area);
}

// ─── Sutherland-Hodgman polygon clipping ─────────────────────────────────

/** Clip subject polygon to the interior of clip polygon (Sutherland-Hodgman) */
export function clipPolygonToPolygon(subject: Point2D[], clip: Point2D[]): Point2D[] {
  const clipNorm = normalizeRing(clip);
  if (clipNorm.length < 3) return subject;
  let output = normalizeRing(subject);
  if (output.length < 3) return output;

  for (let i = 0; i < clipNorm.length; i++) {
    if (output.length < 3) return [];
    const edgeStart = clipNorm[i];
    const edgeEnd = clipNorm[(i + 1) % clipNorm.length];
    output = clipByEdge(output, edgeStart, edgeEnd);
  }
  return output;
}

function clipByEdge(polygon: Point2D[], edgeStart: Point2D, edgeEnd: Point2D): Point2D[] {
  if (polygon.length < 3) return polygon;
  const result: Point2D[] = [];
  const ex = edgeEnd.x - edgeStart.x;
  const ey = edgeEnd.y - edgeStart.y;

  function isInside(p: Point2D): boolean {
    // Point is "inside" if it's on the left side of the directed edge
    return (ex * (p.y - edgeStart.y) - ey * (p.x - edgeStart.x)) >= -0.001;
  }

  function lineIntersect(a: Point2D, b: Point2D): Point2D {
    const ax = b.x - a.x, ay = b.y - a.y;
    const dA = ex * (a.y - edgeStart.y) - ey * (a.x - edgeStart.x);
    const dB = ex * (b.y - edgeStart.y) - ey * (b.x - edgeStart.x);
    const t = dA / (dA - dB);
    return { x: a.x + t * ax, y: a.y + t * ay };
  }

  for (let i = 0; i < polygon.length; i++) {
    const current = polygon[i];
    const next = polygon[(i + 1) % polygon.length];
    const curIn = isInside(current);
    const nxtIn = isInside(next);

    if (curIn) {
      result.push(current);
      if (!nxtIn) result.push(lineIntersect(current, next));
    } else if (nxtIn) {
      result.push(lineIntersect(current, next));
    }
  }
  return result;
}

/** Clamp a point to the nearest position inside a polygon */
export function clampPointToPolygon(point: Point2D, polygon: Point2D[]): Point2D {
  if (isPointInPolygon(point, polygon)) return point;

  const ring = closeRing(normalizeRing(polygon));
  let bestDist = Infinity;
  let bestPoint = point;

  for (let i = 0; i < ring.length - 1; i++) {
    const a = ring[i], b = ring[i + 1];
    const nearest = nearestPointOnSegment(point, a, b);
    const dx = nearest.x - point.x, dy = nearest.y - point.y;
    const dist = dx * dx + dy * dy;
    if (dist < bestDist) {
      bestDist = dist;
      bestPoint = nearest;
    }
  }
  return bestPoint;
}

function nearestPointOnSegment(p: Point2D, a: Point2D, b: Point2D): Point2D {
  const dx = b.x - a.x, dy = b.y - a.y;
  const lenSq = dx * dx + dy * dy;
  if (lenSq < 1e-10) return a;
  const t = Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / lenSq));
  return { x: a.x + t * dx, y: a.y + t * dy };
}
